<script setup>
import { computed, ref } from "vue";
import { setUserDetailsView, setSelectServiceView } from "@/stores/View";
import {
  amountServices,
  paidAmountServices,
  loadSelectServices,
  loadCateringServices,
  paidCateringServices,
  cateringServices,
  addSelectedCateringService,
  removeSelectedCateringService,
  selectServices
} from "@/stores/Services";

import {
  amountServiceMinPrice,
  isUseYourOwnAmountService,
  setUseYourOwnAmountService,
  currency,
  canSelectUseYourOwnAmountService,
  isUseYourOwnAmountServiceDescription,
  orderEditMode,
  canSelectCatering
} from "@/stores/Order";
import { formatPrice } from "@/libraries/Helpers";

import Button from "@/components/Button";
import SpaceTitle from "@/components/SpaceTitle";
import Price from "@/components/Price";
import ServiceList from "@/components/Service/List";
import Alert from "@/components/Alert.vue";

const allowContinue = computed(() => {
  if (isUseYourOwnAmountService.value) {
    return true;
  }

  if (!amountServiceMinPrice.value) {
    return true;
  }

  if (orderEditMode.value && !canSelectUseYourOwnAmountService.value) {
    return true;
  }

  let selectedPrice = 0;

  paidCateringServices.value.forEach(service => {
    selectedPrice += service.price * service.selected_amount;
  });

  cateringServices.value.forEach(service => {
    if (service.selected) {
      if (service.items.length) {
        service.items.forEach(item => {
          if (item.id === service.selected_item_id) {
            selectedPrice += item.price * service.selected_amount;
          }
        });
      } else {
        selectedPrice += service.price * service.selected_amount;
      }
    }
  });

  return amountServiceMinPrice.value <= selectedPrice;
});

const loading = ref(false);
const nextStep = async () => {
  loading.value = true;
  await loadSelectServices();
  setSelectServiceView();
  loading.value = true;
};

const prevStep = () => {
  setUserDetailsView();
};

if (!canSelectCatering.value && !orderEditMode.value) {
  setUseYourOwnAmountService(true);
}

const productAdd = data => {
  addSelectedCateringService(data.id, data.itemId, data.amount);
};

const productRemove = data => {
  removeSelectedCateringService(data.id, data.itemId);
};

const filteredCateringServices = computed(() => {
  return [...cateringServices.value].filter(service => service.can_select);
});
</script>

<template>
  <div
    class="mb-24"
    v-if="canSelectUseYourOwnAmountService && canSelectCatering"
  >
    <div class="flex flex-wrap -m-6">
      <div class="p-6">
        <Button
          :variant="!isUseYourOwnAmountService ? 'primary' : 'third'"
          @click="setUseYourOwnAmountService(false)"
        >
          {{ __("i_want_skypark_catering") }}
        </Button>
      </div>

      <div class="p-6">
        <Button
          :variant="isUseYourOwnAmountService ? 'primary' : 'third'"
          @click="setUseYourOwnAmountService(true)"
        >
          {{ __("i_want_my_food") }}
        </Button>
      </div>
    </div>
  </div>

  <article
    class="mb-24"
    v-html="isUseYourOwnAmountServiceDescription"
    v-if="isUseYourOwnAmountService && isUseYourOwnAmountServiceDescription"
  ></article>

  <div
    class="mb-24"
    v-if="paidCateringServices.length && !isUseYourOwnAmountService"
  >
    <SpaceTitle>{{ __("title_selected_catering_package") }}</SpaceTitle>

    <ServiceList
      :services="paidCateringServices"
      :locked="true"
      :disabled="true"
    />
  </div>

  <template v-if="!isUseYourOwnAmountService">
    <SpaceTitle>{{ __("title_select_catering_package") }}</SpaceTitle>

    <ServiceList
      v-if="filteredCateringServices.length"
      :services="filteredCateringServices"
      :disabled="loading"
      @productAdd="productAdd"
      @productRemove="productRemove"
    />
    <Alert v-else>
      {{ __("catering_not_available") }}
    </Alert>
  </template>

  <div class="mt-16">
    <div
      class="flex flex-col items-end"
      v-if="
        amountServiceMinPrice &&
          canSelectUseYourOwnAmountService &&
          !allowContinue &&
          !isUseYourOwnAmountService
      "
    >
      <div
        class="flex justify-start items-center text-red-500 font-medium text-right"
      >
        <div class="text-6 uppercase -mt-5px mr-4">
          {{ __("minimum_catering_price") }}
        </div>
        <div class="text-12 min-w-38">
          {{ currency }} {{ formatPrice(amountServiceMinPrice) }}
        </div>
      </div>
    </div>

    <Price />

    <div class="flex justify-end flex-wrap -mx-6 -mb-6 mt-10">
      <div class="w-full p-6 md:w-1/2 lg:w-128">
        <Button
          variant="third"
          size="full"
          :disabled="loading"
          @click="prevStep"
        >
          {{ __("btn_back") }}
        </Button>
      </div>

      <div class="w-full p-6 md:w-1/2 lg:w-128">
        <Button
          variant="secondary"
          size="full"
          :disabled="!allowContinue"
          :loading="loading"
          @click="nextStep"
        >
          {{ __("btn_next") }}
        </Button>
      </div>
    </div>
  </div>
</template>
